import React from 'react';
import { NextSeo } from 'next-seo';
import he from 'he';
import sanitize from 'sanitize-html';

import { GetServerSideProps } from 'next';
import RgpdPage from '../components/templates/RgpdPage/RgpdPage';
import { useTranslation } from '@/src/i18n';
import Text from '../components/atoms/Text/Text';
import { getArticleBySlug } from '@/src/hooks/articles/fetchers';
import NotFoundPage from '@/components/templates/404/NotFoundPage';

type Props = {
  article?: ArticleDTO;
};

const PersonalDataPage = ({ article }: Props) => {
  const { t } = useTranslation();
  if (!article) return <NotFoundPage />;
  return (
    <>
      <NextSeo
        title={t('seo-title_data')}
        description={t('seo-description_data')}
      />
      <RgpdPage>
        <Text
          component="h1"
          variant="title"
          size="medlarge"
          className="legal__big-title"
        >
          {he.decode(article?.title?.rendered)}
        </Text>

        {/* Content */}
        <div
          className="legal-ssr-content"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: sanitize(article.content?.rendered, {
              allowedTags: sanitize.defaults.allowedTags.concat([
                'img',
                'figure',
                'a',
                'h2',
                'figcaption',
              ]),
              allowedAttributes: {
                // we could use ['*'] if it becomes unmaintainable in the future
                figure: ['class'],
                a: ['href'],
                div: ['style', 'class'],
                th: ['scope'],
                '*': ['aria-*'],
              },
              transformTags: {
                a(tagName, attribs) {
                  return {
                    tagName: 'a',
                    attribs: {
                      ...attribs,
                      href: attribs?.href?.replace(
                        'https://www.maiia.com/',
                        '/',
                      ),
                    },
                  };
                },
                th(_tagName, attribs) {
                  return {
                    tagName: 'th',
                    attribs: {
                      ...attribs,
                      scope: 'col',
                    },
                  };
                },
              },
            }),
          }}
        />
      </RgpdPage>
    </>
  );
};

/**
 * Can't use getStaticProps for now
 * see packages/pat-frontend/src/hooks/articles/fetchers.ts
 *  */
export const getServerSideProps: GetServerSideProps = async ({ req }) => {
  const props: Props = {};

  try {
    const isSncfUrl = req.headers.host?.includes('sncf');

    const article: ArticleDTO = await getArticleBySlug(
      isSncfUrl
        ? 'politique-de-protection-des-donnees-utilisateurs-patients-sncf'
        : 'politique-de-protection-des-donnees-utilisateurs-patients',
    );

    props.article = article;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('error prefetching article', err);
    // If we want to block the build, we can throw an error
    // FYI: If an error is thrown AFTER a successful build
    // the cache will not be updated and the server will
    // return the last version
    // throw err;
    return { notFound: true };
  }

  return {
    props,
  };
};

export default PersonalDataPage;
